
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DocumentActionMenu } from './DocumentActionMenu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { FileText, File, FileImage, FileCode, Trash2, User } from 'lucide-react';
import { format } from 'date-fns';

interface DocumentTableProps {
  documents: any[];
  onView: (document: any) => void;
  onDelete: (documentId: string) => void;
  onDownload: (document: any) => void;
}

export const DocumentTable: React.FC<DocumentTableProps> = ({
  documents,
  onView,
  onDelete,
  onDownload
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <FileImage className="h-4 w-4" />;
    if (fileType.includes('pdf')) return <FileText className="h-4 w-4" />;
    if (fileType.includes('text')) return <FileCode className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'certificate': return 'bg-green-500 text-white hover:bg-green-600';
      case 'academic': return 'bg-blue-500 text-white hover:bg-blue-600';
      case 'id': return 'bg-purple-500 text-white hover:bg-purple-600';
      case 'general': return 'bg-gray-600 text-white hover:bg-gray-700';
      default: return 'bg-orange-500 text-white hover:bg-orange-600';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDeleteClick = (document: any) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (documentToDelete) {
      onDelete(documentToDelete.id);
    }
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left p-3 font-medium text-white bg-gray-800">Document</th>
              <th className="text-left p-3 font-medium text-white bg-gray-800">Student</th>
              <th className="text-left p-3 font-medium text-white bg-gray-800">Type</th>
              <th className="text-left p-3 font-medium text-white bg-gray-800">Category</th>
              <th className="text-left p-3 font-medium text-white bg-gray-800">Size</th>
              <th className="text-left p-3 font-medium text-white bg-gray-800">Uploaded</th>
              <th className="text-left p-3 font-medium text-white bg-gray-800">Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id} className="border-b border-gray-200 bg-gray-900">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <span className="text-white">{getFileIcon(document.fileType)}</span>
                    <div>
                      <div className="font-medium text-white">{document.name}</div>
                      {document.description && (
                        <div className="text-sm text-gray-300 truncate max-w-xs">
                          {document.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  {document.studentName ? (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-300" />
                      <div>
                        <div className="font-medium text-sm text-white">{document.studentName}</div>
                        {document.studentId && (
                          <div className="text-xs text-gray-300">ID: {document.studentId}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-300 text-sm">Unassigned</span>
                  )}
                </td>
                <td className="p-3">
                  <span className="text-sm font-mono bg-gray-700 text-white px-2 py-1 rounded">
                    {document.fileName.split('.').pop()?.toUpperCase()}
                  </span>
                </td>
                <td className="p-3">
                  <Badge className={getCategoryBadgeColor(document.category)}>
                    {document.category.charAt(0).toUpperCase() + document.category.slice(1)}
                  </Badge>
                </td>
                <td className="p-3 text-sm text-white">
                  {formatFileSize(document.fileSize)}
                </td>
                <td className="p-3 text-sm text-white">
                  {format(new Date(document.uploadDate), 'MMM dd, yyyy')}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <DocumentActionMenu
                      document={document}
                      onView={() => onView(document)}
                      onDownload={() => onDownload(document)}
                      onDelete={() => handleDeleteClick(document)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(document)}
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/20 border-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{documentToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
